import { OneDataCollection } from '@factorialco/factorial-one-react/dist/experimental'
import { RecordType } from '@factorialco/factorial-one-react/dist/experimental'
import { ImportHistoryDocument } from 'generated/resources/graphql'
import { ImportHistoryQuery } from 'generated/resources/graphql'

import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import buildUrl from 'modules/core/lib/buildUrl'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import {
  getImportTypename,
  mapStatus,
  processedRowsLabel,
} from 'modules/universal_importer/common/helpers'

interface ImportHistoryItem extends RecordType {
  importType: string
  legalEntity?: { __typename?: 'CompaniesLegalEntity'; id: number; legalName: string } | null
  status: string
  processed: number
  total: number
  access: {
    fullName: string
    avatar?: string | null
  }
  updatedAt: string
  errored: number
}

function ImportHistoryDataCollection() {
  const { navigation } = useNavigation()
  const source = useGraphqlDataSource({
    document: ImportHistoryDocument,
    itemUrl: (item) => {
      if (!item.errored) {
        return undefined
      }
      return navigation.settings.universalImporter.universalImporterProcess
        .fullPath({
          importProcessId: Number(item.id),
        })
        .replace(/\*+$/, '')
    },
    mapData: (data: ImportHistoryQuery): ImportHistoryItem[] => {
      return data.universalImporter.importProcessesConnection.edges.map(
        (edge): ImportHistoryItem => edge.node
      )
    },
    mapPagination: (data) => {
      return {
        total: Number(data.universalImporter.importProcessesConnection.totalCount),
      }
    },
    mapVariables: ({ sortings, filters }) => {
      return {
        sort: sortings?.[0],
        filters: filters?.importType?.length
          ? {
              importType: { in: filters.importType },
            }
          : undefined,
        first: 20,
      }
    },
    filters: {
      importType: {
        label: i18n.t('universal_importer.import_history.table.filters.type.title'),
        type: 'in',
        default: true,
        options: {
          options: [
            {
              label: i18n.t(
                'universal_importer.import_history.table.filters.type.options.employee_importer'
              ),
              value: 'employee_importer',
            },
            {
              label: i18n.t(
                'universal_importer.import_history.table.filters.type.options.candidates_importer'
              ),
              value: 'candidates_importer',
            },
            {
              label: i18n.t(
                'universal_importer.import_history.table.filters.type.options.employee_edition'
              ),
              value: 'employee_edition',
            },
            {
              label: i18n.t(
                'universal_importer.import_history.table.filters.type.options.payroll_summary_importer'
              ),
              value: 'payroll_summary_importer',
            },
          ],
        },
      },
    },
    sortings: {
      importType: {
        label: i18n.t('universal_importer.import_history.table.headers.type'),
      },
      status: {
        label: i18n.t('universal_importer.import_history.table.headers.status'),
      },
      processed: {
        label: i18n.t('universal_importer.import_history.table.headers.imported_rows'),
      },
      updatedAt: {
        label: i18n.t('universal_importer.import_history.table.headers.import_date'),
      },
      errored: {
        label: i18n.t('universal_importer.import_history.table.headers.errors'),
      },
    },
  })

  return (
    <OneDataCollection
      source={source}
      visualizations={[
        {
          type: 'table',
          options: {
            columns: [
              {
                label: i18n.t('universal_importer.import_history.table.headers.type'),
                sorting: 'importType',
                render: (item: ImportHistoryItem) => {
                  const importType = getImportTypename(
                    String(item.importType),
                    String(item?.legalEntity?.legalName)
                  )
                  return {
                    type: 'text',
                    value: importType,
                  }
                },
              },
              {
                label: i18n.t('universal_importer.import_history.table.headers.status'),
                sorting: 'status',
                render: (item) => ({
                  type: 'status',
                  value: mapStatus(item.status),
                }),
              },
              {
                label: i18n.t('universal_importer.import_history.table.headers.imported_rows'),
                sorting: 'processed',
                render: (item) => {
                  return {
                    type: 'text',
                    value: processedRowsLabel(item.processed, item.total),
                  }
                },
              },
              {
                label: i18n.t('universal_importer.import_history.table.headers.imported_by'),
                render: (item) => {
                  const [firstName, lastName] = item.access.fullName.split(' ')
                  return {
                    type: 'person',
                    value: {
                      firstName: firstName ?? '',
                      lastName: lastName ?? '',
                      src: item.access.avatar ? buildUrl(item.access.avatar) : '',
                    },
                  }
                },
              },
              {
                label: i18n.t('universal_importer.import_history.table.headers.import_date'),
                sorting: 'updatedAt',
                render: ({ updatedAt }) => ({
                  value: i18nDateToString(
                    stringToDate(updatedAt, 'backend_datetime'),
                    'ui_short_with_time'
                  ),
                  type: 'text',
                }),
              },
              {
                label: i18n.t('universal_importer.import_history.table.headers.errors'),
                sorting: 'errored',
                render: ({ errored }) => ({
                  type: 'status',
                  value: {
                    label: String(errored),
                    status: errored ? 'critical' : 'positive',
                  },
                }),
              },
            ],
          },
        },
      ]}
    />
  )
}

export default ImportHistoryDataCollection
